import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Users, BookOpen, Phone, Mail, BarChart3, Search, 
  Shield, Edit2, Trash2, Award, Plus, X, GraduationCap 
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState({ demo_bookings: [], contacts: [] });
  
  const [activeTab, setActiveTab] = useState("overview");
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [courses, setCourses] = useState([]);
  
  // Modals
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEnrollmentModalOpen, setIsEnrollmentModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  
  // Edit User State
  const [editForm, setEditForm] = useState({ name: "", role: "student", streak: 0, xp: 0 });
  
  // Enrollments State
  const [userEnrollments, setUserEnrollments] = useState([]);
  const [selectedCourseToEnroll, setSelectedCourseToEnroll] = useState("");

  const fetchStatsAndLeads = async () => {
    try {
      const [s, l] = await Promise.all([api.get("/admin/stats"), api.get("/admin/leads")]);
      setStats(s.data);
      setLeads(l.data);
    } catch {
      toast.error("Failed to load dashboard statistics.");
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/admin/users", {
        params: {
          role: roleFilter === "All" ? undefined : roleFilter,
          q: searchQuery || undefined
        }
      });
      setUsers(data);
    } catch {
      toast.error("Failed to load user list.");
    }
  };

  const fetchCourses = async () => {
    try {
      const { data } = await api.get("/courses");
      setCourses(data);
    } catch {
      toast.error("Failed to load courses list.");
    }
  };

  useEffect(() => {
    if (user?.role === "admin") {
      fetchStatsAndLeads();
      fetchCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user?.role === "admin" && activeTab === "users") {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeTab, roleFilter, searchQuery]);

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/users/${selectedUser.user_id}`, {
        name: editForm.name,
        streak: Number(editForm.streak),
        xp: Number(editForm.xp)
      });
      
      if (editForm.role !== selectedUser.role) {
        await api.put(`/admin/users/${selectedUser.user_id}/role`, {
          role: editForm.role
        });
      }
      
      toast.success("User updated successfully.");
      setIsEditModalOpen(false);
      fetchUsers();
      fetchStatsAndLeads();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to update user.";
      toast.error(msg);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/admin/users/${selectedUser.user_id}`);
      toast.success("User deleted successfully.");
      setIsDeleteConfirmOpen(false);
      fetchUsers();
      fetchStatsAndLeads();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to delete user.";
      toast.error(msg);
    }
  };

  const handleOpenEnrollments = (u) => {
    setSelectedUser(u);
    fetchUserEnrollments(u.user_id);
    setIsEnrollmentModalOpen(true);
  };

  const fetchUserEnrollments = async (userId) => {
    try {
      const { data } = await api.get(`/admin/users/${userId}/enrollments`);
      setUserEnrollments(data);
    } catch {
      toast.error("Failed to fetch user enrollments.");
    }
  };

  const handleEnrollUser = async (e) => {
    e.preventDefault();
    if (!selectedCourseToEnroll) return;
    try {
      await api.post("/admin/enrollments", {
        user_id: selectedUser.user_id,
        course_id: selectedCourseToEnroll
      });
      toast.success("User enrolled successfully.");
      setSelectedCourseToEnroll("");
      fetchUserEnrollments(selectedUser.user_id);
      fetchStatsAndLeads();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to enroll user.";
      toast.error(msg);
    }
  };

  const handleUnenrollUser = async (enrollmentId) => {
    try {
      await api.delete(`/admin/enrollments/${enrollmentId}`);
      toast.success("User unenrolled successfully.");
      fetchUserEnrollments(selectedUser.user_id);
      fetchStatsAndLeads();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to unenroll user.";
      toast.error(msg);
    }
  };

  const handleOpenEdit = (u) => {
    setSelectedUser(u);
    setEditForm({
      name: u.name,
      role: u.role,
      streak: u.streak || 0,
      xp: u.xp || 0
    });
    setIsEditModalOpen(true);
  };

  const handleOpenDelete = (u) => {
    setSelectedUser(u);
    setIsDeleteConfirmOpen(true);
  };

  if (user?.role !== "admin") {
    return (
      <main className="pt-28 px-4 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-3xl p-10 text-center shadow-xl border border-slate-100">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8" />
          </div>
          <h2 className="font-satoshi font-black text-2xl text-[#0A192F]">Access Restricted</h2>
          <p className="mt-3 text-slate-500 font-inter text-sm leading-relaxed">
            This workspace is restricted to administrators only. Please sign in using an authorized administrator account.
          </p>
          <p className="mt-4 font-bold text-xs uppercase tracking-widest text-[#FF6B35] bg-orange-50 px-4 py-2 rounded-xl w-fit mx-auto">
            admin@alearnindia.com
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-28 pb-24 bg-[#F8F9FA] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-b border-slate-200 pb-8">
          <div>
            <h1 className="font-satoshi font-black text-4xl text-[#0A192F]">Master Control Center</h1>
            <p className="text-slate-500 font-inter mt-1.5 text-sm">
              Complete administrative workspace for A Learn India platform management.
            </p>
          </div>
          
          {/* Tab Toggles */}
          <div className="flex bg-slate-200/60 p-1 rounded-2xl w-fit self-start md:self-auto border border-slate-200">
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-6 py-2.5 rounded-xl font-bold font-poppins text-sm transition-all duration-300 ${
                activeTab === "overview"
                  ? "bg-white text-[#0A192F] shadow-md"
                  : "text-slate-600 hover:text-[#0A192F]"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab("users")}
              className={`px-6 py-2.5 rounded-xl font-bold font-poppins text-sm transition-all duration-300 ${
                activeTab === "users"
                  ? "bg-white text-[#0A192F] shadow-md"
                  : "text-slate-600 hover:text-[#0A192F]"
              }`}
            >
              User Management
            </button>
          </div>
        </div>

        {/* Tab 1: Overview Panel */}
        {activeTab === "overview" && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-8"
          >
            {/* Statistics Row */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
              {[
                { i: Users, t: "Total Students", v: stats?.total_students || 0, bg: "from-blue-500/10 to-indigo-500/10", col: "text-blue-600" },
                { i: GraduationCap, t: "Instructors", v: stats?.total_instructors || 0, bg: "from-purple-500/10 to-fuchsia-500/10", col: "text-purple-600" },
                { i: BookOpen, t: "Courses Active", v: stats?.total_courses || 0, bg: "from-amber-500/10 to-orange-500/10", col: "text-amber-600" },
                { i: BarChart3, t: "Enrollments", v: stats?.total_enrollments || 0, bg: "from-emerald-500/10 to-teal-500/10", col: "text-emerald-600" },
                { i: Phone, t: "Demo Bookings", v: stats?.demo_bookings || 0, bg: "from-rose-500/10 to-pink-500/10", col: "text-rose-600" },
                { i: Mail, t: "Contacts Received", v: stats?.contacts || 0, bg: "from-sky-500/10 to-cyan-500/10", col: "text-sky-600" },
              ].map((s, idx) => (
                <div key={idx} className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100/80 hover:shadow-md transition duration-300">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-tr ${s.bg} flex items-center justify-center ${s.col}`}>
                    <s.i className="w-5 h-5" />
                  </div>
                  <p className="mt-4 font-satoshi font-black text-3xl text-[#0A192F]">{s.v}</p>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 font-poppins mt-1">{s.t}</p>
                </div>
              ))}
            </div>

            {/* Leads Panel */}
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* Recent Demo Bookings */}
              <div className="bg-white rounded-[32px] p-8 shadow-sm border border-slate-100/80">
                <div className="flex items-center justify-between border-b border-slate-50 pb-5 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-50 text-[#FF6B35] flex items-center justify-center">
                      <Phone className="w-4 h-4" />
                    </div>
                    <h2 className="font-satoshi font-bold text-xl text-[#0A192F]">Recent Demo Bookings</h2>
                  </div>
                  <span className="text-xs font-black text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                    {leads.demo_bookings.length} leads
                  </span>
                </div>
                
                <ul className="space-y-4 max-h-[480px] overflow-y-auto pr-2">
                  {leads.demo_bookings.map((b) => (
                    <li key={b.id} className="p-4 bg-slate-50/50 rounded-2xl border border-slate-100 hover:border-[#FF6B35]/20 transition duration-200">
                      <div className="flex justify-between items-start gap-4">
                        <div>
                          <h4 className="font-poppins font-bold text-[#0A192F] text-sm">{b.name}</h4>
                          <p className="text-xs text-slate-500 font-medium mt-0.5">{b.email} · {b.phone}</p>
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-widest bg-orange-50 text-[#FF6B35] px-2.5 py-1 rounded-md">
                          {b.course}
                        </span>
                      </div>
                      {b.message && (
                        <p className="text-xs text-slate-500 italic mt-2.5 bg-white p-2.5 rounded-lg border border-slate-100 leading-relaxed font-inter">
                          "{b.message}"
                        </p>
                      )}
                    </li>
                  ))}
                  {leads.demo_bookings.length === 0 && (
                    <div className="text-center py-12 text-slate-400 font-inter text-sm">No bookings recorded yet.</div>
                  )}
                </ul>
              </div>

              {/* Contact Messages */}
              <div className="bg-white rounded-[32px] p-8 shadow-sm border border-slate-100/80">
                <div className="flex items-center justify-between border-b border-slate-50 pb-5 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-[#0056b3] flex items-center justify-center">
                      <Mail className="w-4 h-4" />
                    </div>
                    <h2 className="font-satoshi font-bold text-xl text-[#0A192F]">Contact Messages</h2>
                  </div>
                  <span className="text-xs font-black text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                    {leads.contacts.length} entries
                  </span>
                </div>
                
                <ul className="space-y-4 max-h-[480px] overflow-y-auto pr-2">
                  {leads.contacts.map((c) => (
                    <li key={c.id} className="p-4 bg-slate-50/50 rounded-2xl border border-slate-100 hover:border-blue-500/20 transition duration-200">
                      <div>
                        <div className="flex justify-between items-start gap-4">
                          <h4 className="font-poppins font-bold text-[#0A192F] text-sm">{c.name}</h4>
                          <span className="text-[10px] font-black uppercase tracking-widest bg-blue-50 text-[#0056b3] px-2.5 py-1 rounded-md">
                            {c.subject}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 font-medium mt-0.5">{c.email} {c.phone && `· ${c.phone}`}</p>
                      </div>
                      <p className="text-xs text-slate-600 mt-3 bg-white p-3 rounded-lg border border-slate-100 leading-relaxed font-inter">
                        {c.message}
                      </p>
                    </li>
                  ))}
                  {leads.contacts.length === 0 && (
                    <div className="text-center py-12 text-slate-400 font-inter text-sm">No messages received yet.</div>
                  )}
                </ul>
              </div>
            </div>
          </motion.div>
        )}

        {/* Tab 2: User Management Panel */}
        {activeTab === "users" && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-8"
          >
            {/* Filters Bar */}
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100/80 flex flex-col md:flex-row gap-4 items-center justify-between">
              
              {/* Search */}
              <div className="relative w-full md:max-w-md">
                <Search className="w-4 h-4 text-slate-400 absolute left-4.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-slate-50 rounded-2xl border border-slate-200/80 placeholder:text-slate-400 focus:outline-none focus:border-[#FF6B35] focus:bg-white text-sm font-poppins font-medium transition duration-200"
                />
              </div>

              {/* Role Dropdown */}
              <div className="flex items-center gap-3 w-full md:w-auto self-stretch md:self-auto justify-end">
                <span className="text-xs font-black text-slate-400 uppercase tracking-widest font-poppins hidden sm:inline">
                  Filter Role:
                </span>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-4 py-3 bg-slate-50 border border-slate-200/80 rounded-2xl text-sm font-bold font-poppins text-slate-700 focus:outline-none focus:border-[#FF6B35] focus:bg-white cursor-pointer transition duration-200 w-full sm:w-48"
                >
                  <option value="All">All Roles</option>
                  <option value="student">Students</option>
                  <option value="instructor">Instructors</option>
                  <option value="admin">Administrators</option>
                </select>
              </div>
            </div>

            {/* Users Data Table */}
            <div className="mt-6 bg-white rounded-[32px] shadow-sm border border-slate-100/80 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="px-6 py-4.5 text-[10px] font-black uppercase tracking-widest text-slate-400 font-poppins">User Details</th>
                      <th className="px-6 py-4.5 text-[10px] font-black uppercase tracking-widest text-slate-400 font-poppins">System Role</th>
                      <th className="px-6 py-4.5 text-[10px] font-black uppercase tracking-widest text-slate-400 font-poppins text-center">Streak</th>
                      <th className="px-6 py-4.5 text-[10px] font-black uppercase tracking-widest text-slate-400 font-poppins text-center">Total XP</th>
                      <th className="px-6 py-4.5 text-[10px] font-black uppercase tracking-widest text-slate-400 font-poppins">Joined Date</th>
                      <th className="px-6 py-4.5 text-[10px] font-black uppercase tracking-widest text-slate-400 font-poppins text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {users.map((u) => (
                      <tr key={u.user_id} className="hover:bg-slate-50/40 transition duration-150">
                        {/* User Details */}
                        <td className="px-6 py-4.5">
                          <div className="flex items-center gap-3.5">
                            <img
                              src={u.picture || `https://api.dicebear.com/7.x/initials/svg?seed=${u.name}`}
                              alt={u.name}
                              className="w-10 h-10 rounded-full border border-slate-100 bg-slate-50 shrink-0"
                            />
                            <div>
                              <p className="font-poppins font-bold text-slate-800 text-sm">{u.name}</p>
                              <p className="font-inter text-slate-400 text-xs mt-0.5">{u.email}</p>
                            </div>
                          </div>
                        </td>

                        {/* System Role */}
                        <td className="px-6 py-4.5">
                          {u.role === "admin" && (
                            <span className="text-[10px] font-black uppercase tracking-wider bg-orange-50 text-[#FF6B35] px-2.5 py-1 rounded-md border border-orange-100">
                              Admin
                            </span>
                          )}
                          {u.role === "instructor" && (
                            <span className="text-[10px] font-black uppercase tracking-wider bg-purple-50 text-purple-600 px-2.5 py-1 rounded-md border border-purple-100">
                              Instructor
                            </span>
                          )}
                          {u.role === "student" && (
                            <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-600 px-2.5 py-1 rounded-md border border-emerald-100">
                              Student
                            </span>
                          )}
                        </td>

                        {/* Streak */}
                        <td className="px-6 py-4.5 text-center">
                          <span className="font-poppins font-extrabold text-slate-700 text-sm">
                            🔥 {u.streak || 0}
                          </span>
                        </td>

                        {/* XP */}
                        <td className="px-6 py-4.5 text-center font-poppins font-extrabold text-slate-700 text-sm">
                          ⚡ {u.xp || 0}
                        </td>

                        {/* Joined Date */}
                        <td className="px-6 py-4.5 font-inter text-slate-400 text-xs">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric"
                          }) : "N/A"}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleOpenEnrollments(u)}
                              title="Manage Course Enrollments"
                              className="w-8.5 h-8.5 bg-slate-100 hover:bg-amber-50 hover:text-[#FF6B35] text-slate-500 rounded-xl flex items-center justify-center transition duration-200"
                            >
                              <Award className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleOpenEdit(u)}
                              title="Edit User Profile"
                              className="w-8.5 h-8.5 bg-slate-100 hover:bg-blue-50 hover:text-[#0056b3] text-slate-500 rounded-xl flex items-center justify-center transition duration-200"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleOpenDelete(u)}
                              title="Delete User"
                              disabled={u.user_id === user.user_id}
                              className={`w-8.5 h-8.5 rounded-xl flex items-center justify-center transition duration-200 ${
                                u.user_id === user.user_id
                                  ? "bg-slate-100 text-slate-300 cursor-not-allowed"
                                  : "bg-slate-100 hover:bg-red-50 hover:text-red-500 text-slate-500"
                              }`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center py-16 text-slate-400 font-inter text-sm">
                          No users found matching your search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

      </div>

      {/* ================= MODALS SECTION ================= */}
      <AnimatePresence>
        
        {/* Modal 1: Edit Profile */}
        {isEditModalOpen && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsEditModalOpen(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            
            {/* Content Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="relative w-full max-w-lg bg-white rounded-[32px] p-8 shadow-2xl border border-slate-100 overflow-hidden z-10"
            >
              <div className="flex items-center justify-between border-b border-slate-100 pb-5 mb-6">
                <h3 className="font-satoshi font-bold text-xl text-[#0A192F]">Edit User Profile</h3>
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="w-8 h-8 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 flex items-center justify-center transition duration-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleUpdateUser} className="space-y-5">
                <div>
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2 font-poppins">
                    Full Name
                  </label>
                  <input
                    type="text"
                    required
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 placeholder:text-slate-400 focus:outline-none focus:border-[#FF6B35] focus:bg-white text-sm font-poppins font-medium transition-all"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2 font-poppins">
                    System Role
                  </label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#FF6B35] focus:bg-white text-sm font-bold font-poppins text-slate-700 cursor-pointer transition-all"
                  >
                    <option value="student">Student</option>
                    <option value="instructor">Instructor</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2 font-poppins">
                      Streak Count
                    </label>
                    <input
                      type="number"
                      min={0}
                      required
                      value={editForm.streak}
                      onChange={(e) => setEditForm({ ...editForm, streak: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#FF6B35] focus:bg-white text-sm font-poppins font-bold text-slate-700 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2 font-poppins">
                      Experience Points (XP)
                    </label>
                    <input
                      type="number"
                      min={0}
                      required
                      value={editForm.xp}
                      onChange={(e) => setEditForm({ ...editForm, xp: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#FF6B35] focus:bg-white text-sm font-poppins font-bold text-slate-700 transition-all"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-8 border-t border-slate-100 pt-6">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-600 font-extrabold py-3.5 rounded-xl transition text-sm font-poppins"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-[#FF6B35] text-white font-extrabold py-3.5 rounded-xl hover:bg-orange-600 transition shadow-lg shadow-orange-500/20 text-sm font-poppins"
                  >
                    Save Modifications
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Modal 2: Manage Enrollments */}
        {isEnrollmentModalOpen && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsEnrollmentModalOpen(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />

            {/* Content Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="relative w-full max-w-2xl bg-white rounded-[32px] p-8 shadow-2xl border border-slate-100 overflow-hidden z-10"
            >
              <div className="flex items-center justify-between border-b border-slate-100 pb-5 mb-6">
                <div>
                  <h3 className="font-satoshi font-bold text-xl text-[#0A192F]">Course Enrollments</h3>
                  <p className="text-xs text-slate-400 font-medium font-poppins mt-0.5">
                    Managing access for {selectedUser.name} ({selectedUser.email})
                  </p>
                </div>
                <button
                  onClick={() => setIsEnrollmentModalOpen(false)}
                  className="w-8 h-8 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 flex items-center justify-center transition duration-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Add New Enrollment Section */}
              <form onSubmit={handleEnrollUser} className="bg-slate-50 p-5 rounded-2xl border border-slate-100 flex flex-col sm:flex-row gap-3 items-end mb-6">
                <div className="flex-1 w-full">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2 font-poppins">
                    Manually Assign New Course
                  </label>
                  <select
                    required
                    value={selectedCourseToEnroll}
                    onChange={(e) => setSelectedCourseToEnroll(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#FF6B35] text-xs font-bold font-poppins text-slate-700 cursor-pointer"
                  >
                    <option value="">Select a course to enroll...</option>
                    {courses
                      .filter(c => !userEnrollments.some(e => e.course_id === c.course_id))
                      .map(c => (
                        <option key={c.course_id} value={c.course_id}>
                          {c.title} {c.price > 0 ? `(₹${c.price})` : "(Academic Tuition)"}
                        </option>
                      ))}
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={!selectedCourseToEnroll}
                  className="w-full sm:w-auto bg-[#FF6B35] disabled:bg-slate-200 text-white disabled:text-slate-400 font-extrabold px-5 py-2.5 rounded-xl hover:bg-orange-600 transition text-xs font-poppins flex items-center justify-center gap-1.5 shrink-0"
                >
                  <Plus className="w-4 h-4" /> Assign Access
                </button>
              </form>

              {/* Active Enrollments List */}
              <div>
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider mb-3.5 font-poppins">
                  Currently Enrolled Courses ({userEnrollments.length})
                </h4>
                <div className="space-y-3.5 max-h-[300px] overflow-y-auto pr-1">
                  {userEnrollments.map((enrollment) => (
                    <div
                      key={enrollment.enrollment_id}
                      className="p-4 bg-white border border-slate-100 rounded-2xl flex items-center justify-between gap-4 hover:border-slate-200 transition"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={enrollment.course_image}
                          alt=""
                          className="w-10 h-7 object-cover rounded-md bg-slate-50 border border-slate-100"
                        />
                        <div>
                          <h5 className="font-poppins font-bold text-slate-800 text-xs leading-normal">
                            {enrollment.course_title}
                          </h5>
                          <p className="text-[10px] font-semibold text-slate-400 mt-0.5">
                            Progress: {enrollment.progress}% · Enrolled: {new Date(enrollment.enrolled_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnenrollUser(enrollment.enrollment_id)}
                        title="Revoke Course Access"
                        className="w-7.5 h-7.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg flex items-center justify-center transition shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {userEnrollments.length === 0 && (
                    <div className="text-center py-10 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200 text-slate-400 text-xs font-poppins">
                      This user is not enrolled in any courses yet.
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8 border-t border-slate-100 pt-6">
                <button
                  type="button"
                  onClick={() => setIsEnrollmentModalOpen(false)}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-600 font-extrabold px-6 py-3 rounded-xl transition text-sm font-poppins"
                >
                  Close Panel
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Modal 3: Delete Confirmation */}
        {isDeleteConfirmOpen && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsDeleteConfirmOpen(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />

            {/* Content Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="relative w-full max-w-md bg-white rounded-[32px] p-8 shadow-2xl border border-slate-100 overflow-hidden z-10"
            >
              <div className="w-14 h-14 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mb-6">
                <Trash2 className="w-7 h-7" />
              </div>

              <h3 className="font-satoshi font-black text-2xl text-[#0A192F]">Permanently Delete User?</h3>
              <p className="mt-3 text-slate-500 font-inter text-sm leading-relaxed">
                Are you sure you want to delete **{selectedUser.name}** ({selectedUser.email})? 
                This action is irreversible. All their enrollments, progress data, and learning history will be permanently erased.
              </p>

              <div className="flex gap-3 mt-8">
                <button
                  type="button"
                  onClick={() => setIsDeleteConfirmOpen(false)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-600 font-extrabold py-3.5 rounded-xl transition text-sm font-poppins"
                >
                  No, Keep User
                </button>
                <button
                  type="button"
                  onClick={handleDeleteUser}
                  className="flex-1 bg-red-500 text-white font-extrabold py-3.5 rounded-xl hover:bg-red-600 transition shadow-lg shadow-red-500/20 text-sm font-poppins"
                >
                  Yes, Delete User
                </button>
              </div>
            </motion.div>
          </div>
        )}

      </AnimatePresence>
    </main>
  );
}
